import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import companyService from "../../../../services/CompanyService";
import permissionsService from "services/permissionsService";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import relatoriosPowerBiService from "services/RelatoriosPowerBiService";
import contasPowerBiService from "services/ContasPowerBiService";
import Swal from "sweetalert2";
import { IconButton, Tooltip } from "@mui/material";
import { PencilSimple, Trash, Plus } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const RelatoriosPowerBiConfig = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Estado para controlar as tabs - inicializa baseado na URL
	const [activeTab, setActiveTab] = useState(() => {
		const tab = searchParams.get('tab');
		return tab === 'contas' ? "2" : "1";
	});

	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS_RELATORIOS = {
		name: { min: 200 },           // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		actions: { min: 100, max: 100 }, // Largura mín/máx da coluna "Ações" (em pixels)
	};

	const COLUMN_WIDTHS_CONTAS = {
		name: { min: 150, },           // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		username: { min: 200},       // Largura mínima da coluna "Username" (sem máximo - cresce livremente)
		password: { min: 200,},      // Largura mínima da coluna "Password" (sem máximo - cresce livremente)
		actions: { min: 100, max: 100 }, // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	// Estados para relatórios
	const [dataRelatorios, setDataRelatorios] = useState([]);
	// Estados para contas
	const [dataContas, setDataContas] = useState([]);
	
	const [temPermissaoEditar, setTemPermissaoEditar] = useState(false);
	const [temPermissaoAdicionar, setTemPermissaoAdicionar] = useState(false);
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);
	const [temPermissaoExcluir, setTemPermissaoExcluir] = useState(false);

	const navigate = useNavigate();

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth: getColumnWidthRelatorios } = useTableZoom(COLUMN_WIDTHS_RELATORIOS);
	const { getColumnWidth: getColumnWidthContas } = useTableZoom(COLUMN_WIDTHS_CONTAS);

	document.title = "Senda | Relatórios Power BI";

	// Função para alternar entre tabs
	const toggle = (tab) => {
		if (activeTab !== tab) {
			setActiveTab(tab);
			// Atualiza a URL baseada na aba selecionada
			if (tab === "2") {
				setSearchParams({ tab: 'contas' });
			} else {
				setSearchParams({});
			}
		}
	};

	const columnsRelatorios = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidthRelatorios('name'),
				minWidthPixels: COLUMN_WIDTHS_RELATORIOS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidthRelatorios('actions'),
				minWidthPixels: COLUMN_WIDTHS_RELATORIOS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS_RELATORIOS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						{temPermissaoEditar && (
							<Tooltip title="Editar" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() => editarItem(row.original)}
								>
									<PencilSimple size={14} />
								</IconButton>
							</Tooltip>
						)}
						{temPermissaoExcluir && (
							<Tooltip title="Excluir" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() =>
										deletarItem(row.original.id)
									}
								>
									<Trash size={14} />
								</IconButton>
							</Tooltip>
						)}
					</div>
				),
				align: "center",
			},
		],
		[temPermissaoEditar, temPermissaoExcluir, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const columnsContas = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidthContas('name'),
				minWidthPixels: COLUMN_WIDTHS_CONTAS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: "Usuário",
				accessorKey: "username",
				filterFn: "contains",
				widthPixels: getColumnWidthContas('username'),
				minWidthPixels: COLUMN_WIDTHS_CONTAS.username.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Senha",
				accessorKey: "password",
				filterFn: "contains",
				widthPixels: getColumnWidthContas('password'),
				minWidthPixels: COLUMN_WIDTHS_CONTAS.password.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidthContas('actions'),
				minWidthPixels: COLUMN_WIDTHS_CONTAS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS_CONTAS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						{temPermissaoEditar && (
							<Tooltip title="Editar" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() => editarConta(row.original)}
								>
									<PencilSimple size={14} />
								</IconButton>
							</Tooltip>
						)}
						{temPermissaoExcluir && (
							<Tooltip title="Excluir" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() =>
										deletarConta(row.original.id)
									}
								>
									<Trash size={14} />
								</IconButton>
							</Tooltip>
						)}
					</div>
				),
				align: "center",
			},
		],
		[temPermissaoEditar, temPermissaoExcluir, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		navigate(`/relatorios-power-bi-config/form/${rowData.id}`);
	};

	const editarConta = (rowData) => {
		navigate(`/relatorios-power-bi-config/form-contas/${rowData.id}?tab=contas`);
	};

	const deletarItem = async (id) => {
		const result = await SendaCustomSwal.fire({
			title: "Tem certeza que deseja excluir?",
			text: "Você não poderá reverter isso!",
			iconHtml: '<i class="warning-icon"></i>',
			showCancelButton: true,
			confirmButtonText: "Sim, excluir!",
			cancelButtonText: "Cancelar",
			customClass: {
				cancelButton: "custom-cancel-button",
				confirmButton: "custom-confirm-button",
			},
		});

		if (result.isConfirmed) {
			try {
				await relatoriosPowerBiService.delete(id);
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: "Relatório excluído com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
				// Atualize a lista de relatórios após a exclusão
				getItems();
			} catch (error) {
				await SendaCustomSwal.fire({
					title: "Erro",
					text: "Erro ao excluir relatório!",
					iconHtml: '<i class="error-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
		}
	};

	const deletarConta = async (id) => {
		const result = await SendaCustomSwal.fire({
			title: "Tem certeza que deseja excluir?",
			text: "Você não poderá reverter isso!",
			iconHtml: '<i class="warning-icon"></i>',
			showCancelButton: true,
			confirmButtonText: "Sim, excluir!",
			cancelButtonText: "Cancelar",
			customClass: {
				cancelButton: "custom-cancel-button",
				confirmButton: "custom-confirm-button",
			},
		});

		if (result.isConfirmed) {
			try {
				await contasPowerBiService.delete(id);
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: "Conta excluída com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
				// Atualize a lista de contas após a exclusão
				getContas();
			} catch (error) {
				await SendaCustomSwal.fire({
					title: "Erro",
					text: "Erro ao excluir conta!",
					iconHtml: '<i class="error-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
		}
	};

	const getItems = async () => {
		try {
			const response = await relatoriosPowerBiService.getAll();
			setDataRelatorios(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getContas = async () => {
		try {
			const response = await contasPowerBiService.getAll();
			setDataContas(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "PowerBIUpdate") {
					setTemPermissaoEditar(true);
				}
				if (permission.role === "PowerBICreate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "PowerBIList") {
					setTemPermissaoVisualizar(true);
				}
				if (permission.role === "PowerBIDelete") {
					setTemPermissaoExcluir(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		getPermissions();
		getItems();
		getContas();
	}, []);

	if (!temPermissaoVisualizar) return;

	return (
		<div className="page-content">
			<Breadcrumbs
				title={activeTab === "1" ? "Relatórios Power BI" : "Contas Power BI"}
				breadcrumbItem={activeTab === "1" ? "Relatórios Power BI" : "Contas Power BI"}
			/>
			
			<div
				className="container-fluid"
				style={{
					backgroundColor: "white",
					padding: 20,
					borderRadius: 10,
					marginTop: 25,
				}}
			>				
				<Nav tabs style={{ marginBottom: 20 }}>
					<NavItem>
						<NavLink
							className={classnames({ active: activeTab === "1" })}
							onClick={() => { toggle("1"); }}
							style={{ cursor: "pointer", color: activeTab === "1" ? "#2a3042" : "inherit" }}
						>
							Relatórios Power BI
						</NavLink>
					</NavItem>
					<NavItem>
						<NavLink
							className={classnames({ active: activeTab === "2" })}
							onClick={() => { toggle("2"); }}
							style={{ cursor: "pointer", color: activeTab === "2" ? "#2a3042" : "inherit" }}
						>
							Contas Power BI
						</NavLink>
					</NavItem>
				</Nav>

				<TabContent activeTab={activeTab}>
					<TabPane tabId="1">
						{temPermissaoAdicionar && (
							<div className="page-title-right" style={{ display: "flex", justifyContent: "end", marginBottom: 20 }}>
								<Button
									onClick={() => navigate("/relatorios-power-bi-config/form")}
									style={{
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
										border: "1px solid #ccc",
										borderRadius: "4px",
										padding: "8px 16px"
									}}
								>
									<Plus size={16} /> Adicionar Relatório Power BI
								</Button>
							</div>
						)}

						<DynamicMaterialTable columns={columnsRelatorios} data={dataRelatorios} />
					</TabPane>
					<TabPane tabId="2">
						{temPermissaoAdicionar && (
							<div className="page-title-right" style={{ display: "flex", justifyContent: "end", marginBottom: 20 }}>
								<Button
									onClick={() => navigate("/relatorios-power-bi-config/form-contas?tab=contas")}
									style={{
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
										border: "1px solid #ccc",
										borderRadius: "4px",
										padding: "8px 16px"
									}}
								>
									<Plus size={16} /> Adicionar Conta Power BI
								</Button>
							</div>
						)}

						<DynamicMaterialTable columns={columnsContas} data={dataContas} />
					</TabPane>
				</TabContent>
			</div>
		</div>
	);
};

export default withTranslation()(RelatoriosPowerBiConfig);
